'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { AtomicContext } from '@contexts/coveo/AtomicContext';
import { BreadcrumbManagerState } from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { useContext } from 'react';
import { interpolateStringWithData } from 'utils';
import xss from 'xss';
import { useBreadcrumbManager } from './hooks/useBreadcrumbManager';
import { useQuerySummary } from './hooks/useQuerySummary';

const TITLE_MICROCOPY_KEY = 'search.page-title';
const CATEGORY_FALLBACK_KEY = 'search.page-title-category-fallback';

const getSelectedCategory = (breadcrumbManager?: BreadcrumbManagerState) => {
  return breadcrumbManager?.categoryFacetBreadcrumbs[0]?.path.find(
    (path) => path.state === 'selected'
  )?.value;
};

const getMicrocopyValues = (microcopy: MicrocopyFragment[]) => {
  const pageTitleMicrocopy = microcopy.find(
    (microcopyFragment) => microcopyFragment.key === TITLE_MICROCOPY_KEY
  );
  const categoryFallbackMicrocopy = microcopy.find(
    (microcopyFragment) => microcopyFragment.key === CATEGORY_FALLBACK_KEY
  );

  return { pageTitleMicrocopy, categoryFallbackMicrocopy };
};

const getCategoryName = (
  searchValuesMicrocopy: MicrocopyFragment[],
  activeCategory?: string
) => {
  if (!activeCategory) {
    return undefined;
  }

  const categoryResourceBundle = microcopySetToResourceBundles(
    searchValuesMicrocopy
  ).ec_category;

  return categoryResourceBundle[activeCategory] ?? activeCategory;
};

export const SearchTitle = ({ context }: { context: AtomicContext }) => {
  const { searchEngine } = useContext(context);
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyFragment[];
  }>();
  const { searchBoxState } = useQuerySummary(searchEngine);
  const { breadcrumbManagerState } = useBreadcrumbManager(searchEngine);
  const activeCategory = getSelectedCategory(breadcrumbManagerState);
  const { categoryFallbackMicrocopy, pageTitleMicrocopy } = getMicrocopyValues(
    searchLabelsMicrocopy
  );
  const categoryName = getCategoryName(searchValuesMicrocopy, activeCategory);
  const searchQuery = searchBoxState?.query;
  const hasResults = searchBoxState?.hasResults;

  if (!searchQuery || !hasResults) {
    return null;
  }

  const title = interpolateStringWithData(
    pageTitleMicrocopy?.value || '{{search}} in {{category}}',
    {
      search: searchQuery,
      category:
        categoryName ?? (categoryFallbackMicrocopy?.value || 'All categories'),
    }
  );
  const sanitizedTitle = xss(title);

  return (
    <h1
      className="header-mid mb-8 font-normal lg:mb-12"
      data-test="search-page-title"
      dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
    />
  );
};
